// BubbleChart.jsx
import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, Paper, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NoDataFound from "../../../dashboardcomponents/Traffic/NoDataFound";
import "./styles.css";

const TAG_COLORS = [
  "#148DFD", "#FF4D4F", "#FA8C16", "#FA541C", "#FAAD14",
  "#FADB14", "#52C41A", "#A0D911", "#9254DE", "#D5DCE1",
  "#5CDBD3", "#405261", "#EB2F96", "#3EE9C7", "#D6EDFF",
];

const BubbleChart = ({ data, title = "Tag performance", description = "Most tag usage", loading = false }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up(1300));
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });
  const positionCacheRef = useRef(new Map());
  const prevDataRef = useRef(null);
  const [renderKey, setRenderKey] = useState(0);
  const isMountedRef = useRef(false);
  const [tooltip, setTooltip] = useState(null);
  const bubbleContainerRef = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    setInitialLoading(false);
  }, []);

  // Check if data is empty
  const isEmpty = !loading && !initialLoading && (!data || data.length === 0);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      prevDataRef.current = data;
      return;
    }

    const dataChanged = JSON.stringify(prevDataRef.current) !== JSON.stringify(data);
    if (dataChanged) {
      positionCacheRef.current.clear();
      prevDataRef.current = data;
      setRenderKey((prev) => prev + 1);
    }
  }, [data]);

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const MAX_BUBBLE_SIZE = isLargeScreen ? 188 : 120;
    const MIN_BUBBLE_SIZE = 40;
    const maxValue = Math.max(...data.map((item) => item.usageCount || item.value || item.count || 0));
    const rawBubbles = data.map((item, index) => {
      const value = item.usageCount || item.value || item.count || 0;
      let size;
      if (value === 0 || maxValue === 0) {
        size = MIN_BUBBLE_SIZE;
      } else if (value === maxValue) {
        size = MAX_BUBBLE_SIZE;
      } else {
        const ratio = value / maxValue;
        size = MIN_BUBBLE_SIZE + ratio * (MAX_BUBBLE_SIZE - MIN_BUBBLE_SIZE);
      }
      return {
        name: item.name || item.group || `Tag ${index + 1}`,
        value,
        size: Math.round(size),
        color: TAG_COLORS[index % TAG_COLORS.length],
      };
    });
    return rawBubbles;
  }, [data, isLargeScreen]);

  const total = useMemo(() => {
    return processedData.reduce((sum, d) => sum + d.value, 0);
  }, [processedData]);

  const handleHover = (event, item) => {
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    let tooltipLeft = mouseX + 10;
    let tooltipTop = mouseY;
    const tooltipWidth = 160;
    if (tooltipLeft + tooltipWidth > window.innerWidth) {
      tooltipLeft = mouseX - tooltipWidth - 10;
    }
    const tooltipHeight = 100;
    if (tooltipTop + tooltipHeight > window.innerHeight) {
      tooltipTop = mouseY - tooltipHeight;
    }
    tooltipLeft = Math.max(10, Math.min(tooltipLeft, window.innerWidth - tooltipWidth - 10));
    tooltipTop = Math.max(10, Math.min(tooltipTop, window.innerHeight - tooltipHeight - 10));
    setTooltip({
      position: { x: tooltipLeft, y: tooltipTop },
      data: item,
    });
  };

  const handleLeave = () => setTooltip(null);

  const updateDimensions = useCallback(() => {
    if (bubbleContainerRef.current) {
      const newWidth = bubbleContainerRef.current.offsetWidth;
      const newHeight = bubbleContainerRef.current.offsetHeight;

      // Only update if dimensions actually changed significantly (more than 10px)
      setDimensions(prev => {
        if (Math.abs(prev.width - newWidth) > 10 || Math.abs(prev.height - newHeight) > 10) {
          return { width: newWidth, height: newHeight };
        }
        return prev;
      });
    }
  }, []);

  useEffect(() => {
    updateDimensions();

    let resizeTimer;
    const debouncedUpdate = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 50); // Faster debounce
    };

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (bubbleContainerRef.current) {
        resizeObserver.observe(bubbleContainerRef.current);
      }
      return () => {
        clearTimeout(resizeTimer);
        resizeObserver.disconnect();
      };
    } else {
      window.addEventListener("resize", debouncedUpdate);
      return () => {
        clearTimeout(resizeTimer);
        window.removeEventListener("resize", debouncedUpdate);
      };
    }
  }, [updateDimensions]);

  const getCirclePackPositions = useCallback((bubbles, width, height) => {
    if (bubbles.length === 0) return [];

    const cacheKey =
      bubbles.map((b) => `${b?.name || ''}-${Math.round(b?.size || 0)}`).join("|") +
      `|${Math.round(width / 100) * 100}x${Math.round(height / 100) * 100}|${isLargeScreen}`;

    if (positionCacheRef.current.has(cacheKey)) {
      return positionCacheRef.current.get(cacheKey);
    }

    const centerX = width / 2;
    const centerY = height / 2;
    const padding = 10;
    const minGap = isLargeScreen ? 12 : 8;
    const packingDensity = 0.82;        // ← increased → bigger bubbles
    const ABSOLUTE_MIN_SIZE = 35;       // ← increased → bigger bubbles
    const maxScaleAttempts = 3;
    let scaleAdjustment = 1;
    let finalPositions = [];
    let lastPositions = [];

    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 43758.5453123;
      return x - Math.floor(x);
    };

    for (let attempt = 0; attempt < maxScaleAttempts; attempt++) {
      // Safety filter + area calculation
      const validBubbles = bubbles.filter(b => b && typeof b === "object" && typeof b.size === "number" && !isNaN(b.size));
      if (validBubbles.length === 0) {
        positionCacheRef.current.set(cacheKey, []);
        return [];
      }

      const availableArea = (width - 2 * padding) * (height - 2 * padding) * packingDensity;
      let sumAreas = 0;
      validBubbles.forEach((b) => {
        const r = b.size / 2;
        sumAreas += Math.PI * r * r;
      });

      let scale = 1;
      if (sumAreas > availableArea) {
        scale = Math.sqrt(availableArea / sumAreas);
      }
      scale *= scaleAdjustment;

      // Scale bubbles
      const scaledBubbles = validBubbles.map((b) => ({
        ...b,
        size: Math.max(ABSOLUTE_MIN_SIZE, b.size * scale),
      }));

      const orderedBubbles = [...scaledBubbles].sort((a, b) => b.size - a.size);
      const positions = [];

      // Largest in center
      if (orderedBubbles.length > 0) {
        const largest = orderedBubbles[0];
        if (largest && typeof largest.size === "number") {
          const offset = 24;
          positions.push({
            ...largest,
            x: centerX + (seededRandom(7) - 0.5) * offset,
            y: centerY + (seededRandom(13) - 0.5) * offset,
          });
        }
      }

      // Helper
      const isValidPosition = (x, y, size) => {
        const r = size / 2;
        if (x - r < padding || x + r > width - padding || y - r < padding || y + r > height - padding) {
          return false;
        }
        for (const pos of positions) {
          if (!pos || typeof pos.size !== "number") continue;
          const dx = pos.x - x;
          const dy = pos.y - y;
          const dist = Math.hypot(dx, dy);
          const minDist = (pos.size / 2 + size / 2) + minGap;
          if (dist < minDist) return false;
        }
        return true;
      };

      // Place remaining bubbles
      for (let i = 1; i < orderedBubbles.length; i++) {
        const bubble = orderedBubbles[i];
        if (!bubble || typeof bubble.size !== "number" || isNaN(bubble.size)) continue; // ← safety

        let placed = false;

        // Strategy 1: radial
        for (let att = 0; att < 100 && !placed; att++) {
          const angle = seededRandom(i * 1800 + att * 37) * Math.PI * 2;
          const distFactor = 0.45 + seededRandom(i * 3400 + att) * 0.55;
          const radius = Math.min(width, height) * 0.18 + distFactor * Math.min(width, height) * 0.55;

          const tx = centerX + Math.cos(angle) * radius;
          const ty = centerY + Math.sin(angle) * radius;

          if (isValidPosition(tx, ty, bubble.size)) {
            positions.push({ ...bubble, x: tx, y: ty });
            placed = true;
          }
        }

        // Strategy 2: near existing
        if (!placed && positions.length > 2) {
          const maxTriesNearby = 4;
          const shuffled = [...Array(positions.length).keys()].sort(() => seededRandom(i * 900 + Date.now()) - 0.5);

          for (let k = 0; k < maxTriesNearby && !placed; k++) {
            const anchor = positions[shuffled[k]];
            if (!anchor) continue;
            const baseDist = (anchor.size + bubble.size) / 2 + minGap * 1.4;

            for (let a = 0; a < 18 && !placed; a++) {
              const ang = seededRandom(i * 2500 + k * 100 + a) * Math.PI * 2;
              const jitter = 0.5 + seededRandom(i * 3100 + a) * 1.2;
              const testDist = baseDist * jitter;

              const tx = anchor.x + Math.cos(ang) * testDist;
              const ty = anchor.y + Math.sin(ang) * testDist;

              if (isValidPosition(tx, ty, bubble.size)) {
                positions.push({ ...bubble, x: tx, y: ty });
                placed = true;
              }
            }
          }
        }

        // Last resort: wide random
        if (!placed) {
          for (let att = 0; att < 200 && !placed; att++) {
            const ang = Math.random() * Math.PI * 2;
            const rad = 50 + Math.random() * Math.min(width, height) * 0.52;

            const tx = centerX + Math.cos(ang) * rad;
            const ty = centerY + Math.sin(ang) * rad;

            if (isValidPosition(tx, ty, bubble.size)) {
              positions.push({ ...bubble, x: tx, y: ty });
              placed = true;
            }
          }
        }

        // Absolute fallback
        if (!placed) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.max(80, Math.min(width, height) * 0.35);
          let x = centerX + Math.cos(angle) * dist;
          let y = centerY + Math.sin(angle) * dist;
          const r = bubble.size / 2 + padding;
          x = Math.max(r, Math.min(width - r, x));
          y = Math.max(r, Math.min(height - r, y));
          positions.push({ ...bubble, x, y });
        }
      }

      // Relaxation + overlap check (with safety)
      const maxRelax = 40;
      for (let iter = 0; iter < maxRelax; iter++) {
        let moved = false;
        for (let i = 0; i < positions.length; i++) {
          for (let j = i + 1; j < positions.length; j++) {
            const a = positions[i];
            const b = positions[j];
            if (!a || !b || typeof a.size !== "number" || typeof b.size !== "number") continue;

            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const d = Math.hypot(dx, dy);
            const minD = (a.size + b.size) / 2 + minGap;

            if (d < minD && d > 0.01) {
              moved = true;
              const push = (minD - d) * 0.45;
              const ux = dx / d;
              const uy = dy / d;
              a.x -= ux * push;
              a.y -= uy * push;
              b.x += ux * push;
              b.y += uy * push;

              // Clamp immediately after push to prevent cutting
              const a_r = a.size / 2 + padding;
              a.x = Math.max(a_r, Math.min(width - a_r, a.x));
              a.y = Math.max(a_r, Math.min(height - a_r, a.y));

              const b_r = b.size / 2 + padding;
              b.x = Math.max(b_r, Math.min(width - b_r, b.x));
              b.y = Math.max(b_r, Math.min(height - b_r, b.y));
            }
          }
        }

        positions.forEach((p) => {
          if (!p || typeof p.size !== "number") return;
          const r = p.size / 2 + padding;
          p.x = Math.max(r, Math.min(width - r, p.x));
          p.y = Math.max(r, Math.min(height - r, p.y));
        });

        if (!moved) break;
      }

      // Final overlap check
      let hasOverlap = false;
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const a = positions[i];
          const b = positions[j];
          if (!a || !b || typeof a.size !== "number" || typeof b.size !== "number") continue;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d = Math.hypot(dx, dy);
          const minD = (a.size + b.size) / 2 + minGap;
          if (d < minD) {
            hasOverlap = true;
            break;
          }
        }
        if (hasOverlap) break;
      }

      lastPositions = positions;

      if (!hasOverlap) {
        finalPositions = positions;
        break;
      }

      scaleAdjustment *= 0.92; // gentler reduction
    }

    if (finalPositions.length === 0 && lastPositions.length > 0) {
      finalPositions = lastPositions;
    }

    // Recenter the entire group to prevent cutting on one side
    if (finalPositions.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      finalPositions.forEach(p => {
        if (!p || typeof p.size !== "number") return;
        minX = Math.min(minX, p.x - p.size / 2);
        maxX = Math.max(maxX, p.x + p.size / 2);
        minY = Math.min(minY, p.y - p.size / 2);
        maxY = Math.max(maxY, p.y + p.size / 2);
      });

      const bboxWidth = maxX - minX;
      const bboxHeight = maxY - minY;

      const shiftX = (width - bboxWidth) / 2 - minX + padding;
      const shiftY = (height - bboxHeight) / 2 - minY + padding;

      finalPositions.forEach(p => {
        if (!p) return;
        p.x += shiftX;
        p.y += shiftY;
      });

      // Clamp again after shift
      finalPositions.forEach(p => {
        if (!p || typeof p.size !== "number") return;
        const r = p.size / 2 + padding;
        p.x = Math.max(r, Math.min(width - r, p.x));
        p.y = Math.max(r, Math.min(height - r, p.y));
      });
    }

    positionCacheRef.current.set(cacheKey, finalPositions);
    return finalPositions;
  }, [isLargeScreen]);

  const positionedData = useMemo(() => {
    return getCirclePackPositions(processedData, dimensions.width, dimensions.height);
  }, [processedData, dimensions, getCirclePackPositions]);

  return (
    <Paper className="bubble-chart-root" elevation={0}>
      <Box className="bubble-chart-content">
        <Typography className="bubble-title">{title}</Typography>
        <Typography className="bubble-description">{description}</Typography>

        <Box className="bubble-container" ref={bubbleContainerRef}>
          {(loading || initialLoading) && (
            <Box className="loading-overlay">
              {[120, 100, 80, 90, 70, 85, 95, 75].map((size, i) => (
                <Box
                  key={i}
                  className="loading-skeleton"
                  sx={{
                    width: `${size}px`,
                    height: `${size}px`,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </Box>
          )}

          {isEmpty ? (
            <Box
              sx={{
                height: '248px',           // ← adjust as needed
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <NoDataFound
                containerStyle={{ height: 'auto' }}
                title="No Data Found"
                isForChart={true}
                showBackButton={false}
              />
            </Box>


          ) : (
            <Box key={renderKey} sx={{ display: (loading || initialLoading) ? 'none' : 'block' }}>
              {positionedData.map((item, index) => {
                const baseFontName = Math.max(6, item.size * 0.16);
                const estLines = Math.ceil(item.name.length / (item.size / baseFontName * 1.2));
                const lengthFactor = Math.max(0.6, 1 / (estLines + 0.5));
                const fontSizeName = Math.min(12, Math.max(6, baseFontName * lengthFactor));
                const useEllipsis = item.size < 50;

                return (
                  <Box
                    key={`${item.name}-${index}`}
                    className="bubbleChart"
                    style={{
                      width: `${item.size}px`,
                      height: `${item.size}px`,
                      backgroundColor: item.color,
                      left: `${item.x}px`,
                      top: `${item.y}px`,
                      transition: 'left 0.3s ease-out, top 0.3s ease-out',
                    }}
                    onMouseEnter={(e) => handleHover(e, item)}
                    onMouseLeave={handleLeave}
                  >
                    <Box className="bubble-label-wrapper">
                      <Typography
                        className="bubble-label"
                        sx={{
                          fontSize: `${fontSizeName}px`,
                          wordBreak: useEllipsis ? "normal" : "break-word",
                          whiteSpace: useEllipsis ? "nowrap" : "normal",
                          overflow: useEllipsis ? "hidden" : "visible",
                          textOverflow: useEllipsis ? "ellipsis" : "clip",
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {tooltip && (
        <Box
          className="bubble-tooltip"
          style={{
            left: `${tooltip.position.x}px`,
            top: `${tooltip.position.y}px`,
          }}
        >
          <Typography className="bubble-tooltip-title">{title}</Typography>
          <Typography className="bubble-tooltip-name">{tooltip.data.name}</Typography>
          <Typography className="bubble-tooltip-usage">
            Usage: {tooltip.data.value}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default BubbleChart;